// GA4 client wrapper. The gtag.js script is loaded directly in index.html;
// this module is only the typed sender API used by the rest of the app.
// Never pass PII (email, name, free-text) — only allowlisted scalar params.

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function isEnabled(): boolean {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

export function trackPageView(path: string, title?: string): void {
  if (!isEnabled()) return;
  window.gtag!('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
  });
}

export function track(eventName: string, params: Record<string, unknown> = {}): void {
  if (!isEnabled()) return;
  window.gtag!('event', eventName, params);
}

// Use gtag('set', ...) — NOT gtag('config', ...) — to attach a user_id.
// Calling gtag('config', ID, {...}) again would re-run stream init and could
// re-enable auto page_view, double-counting visits.
// Pass null (not undefined) on logout — gtag treats `undefined` as a no-op,
// so events would keep being attributed to the previous user_id.
export function identify(userId: string | null): void {
  if (!isEnabled()) return;
  window.gtag!('set', { user_id: userId === null ? null : userId });
}

// Read the GA4 client_id from the `_ga` cookie. Format is
// `GA1.<depth>.<clientId>.<timestamp>` where the actual client_id is the
// last two dot-segments joined. Returning this lets server-side Measurement
// Protocol calls share an identity with the browser session — without it,
// server events show up in GA4 as separate "ghost" users with no attribution.
export function getGaClientId(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
  if (!match) return null;
  const parts = match[1].split('.');
  if (parts.length < 4) return null;
  return `${parts[2]}.${parts[3]}`;
}
